import { useCallback, useState } from 'react'

/**
 * A `useState` whose value persists to `localStorage` (per-browser), so a user's
 * low-stakes view preference — Ratings vs Stats, show-potentials, page size,
 * playback speed, etc. — sticks across reloads instead of resetting to the
 * default on every visit. Same shape as `useState`: `[value, setValue]`, and
 * `setValue` accepts a value or an updater function just like `useState`.
 *
 * Per-device by design: this is for display *chrome*, not data. Data that should
 * follow a user across devices (favorites, watchlists) belongs on the server, not
 * here. Keys follow the `csfbl.<area>.<thing>` convention used elsewhere.
 *
 * Degrades gracefully: if `localStorage` is unavailable (private mode) or the
 * stored value can't be parsed, it falls back to `initial` and stays in-memory.
 */
export function usePersistentState<T>(
  key: string,
  initial: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => readStored(key, initial))

  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved =
          typeof next === 'function' ? (next as (p: T) => T)(prev) : next
        try {
          localStorage.setItem(key, JSON.stringify(resolved))
        } catch {
          /* localStorage unavailable (private mode) — keep the value in-memory only */
        }
        return resolved
      })
    },
    [key],
  )

  return [value, set]
}

function readStored<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    if (raw === null) return fallback
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}
